import React, { useState } from "react";
import { X, Calculator, Check } from "lucide-react";
import { TRASPARENZA_AREE, fasciaTrasparenza } from "../../trasparenzaSchema";
import { adminUpdateBilancio } from "../../api";

const todayISO = () => new Date().toISOString().slice(0, 10);

const buildInitial = (tr) => {
  const state = {};
  TRASPARENZA_AREE.forEach((area) => {
    state[area.key] = {};
    area.criteri.forEach((c) => {
      const src = ((tr?.aree || {})[area.key] || {}).criteri || {};
      const cur = src[c.key];
      state[area.key][c.key] = {
        valore: cur ? !!cur.valore : false,
        punti: cur ? cur.punti ?? 0 : 0,
      };
    });
  });
  return state;
};

const TrasparenzaEditor = ({ bilancio, onClose, onSaved }) => {
  const tr = bilancio.trasparenza || {};
  const [crit, setCrit] = useState(() => buildInitial(tr));
  const [cosaManca, setCosaManca] = useState((tr.cosa_manca || []).join("\n"));
  const [forza, setForza] = useState((tr.punti_di_forza_trasparenza || []).join("\n"));
  const [dataRil, setDataRil] = useState(tr.data_rilevazione || todayISO());
  const [total, setTotal] = useState(tr.punteggio_totale || 0);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const toggle = (areaKey, c) => {
    setCrit((s) => {
      const cur = s[areaKey][c.key];
      const nowVal = !cur.valore;
      return {
        ...s,
        [areaKey]: { ...s[areaKey], [c.key]: { valore: nowVal, punti: nowVal ? c.max : 0 } },
      };
    });
  };
  const setPunti = (areaKey, c, val) => {
    const n = Math.max(0, Math.min(c.max, parseInt(val || "0", 10) || 0));
    setCrit((s) => ({
      ...s,
      [areaKey]: { ...s[areaKey], [c.key]: { valore: n > 0, punti: n } },
    }));
  };

  const areaScore = (area) =>
    area.criteri.reduce((sum, c) => sum + (crit[area.key][c.key].punti || 0), 0);

  const calcola = () => {
    const t = TRASPARENZA_AREE.reduce((sum, area) => sum + areaScore(area), 0);
    setTotal(t);
  };

  const save = async () => {
    setBusy(true);
    setMsg(null);
    const aree = {};
    TRASPARENZA_AREE.forEach((area) => {
      const criteri = {};
      area.criteri.forEach((c) => {
        criteri[c.key] = { valore: crit[area.key][c.key].valore, punti: crit[area.key][c.key].punti, max: c.max };
      });
      aree[area.key] = { punteggio: areaScore(area), max: area.max, criteri };
    });
    const trasparenza = {
      punteggio_totale: TRASPARENZA_AREE.reduce((sum, area) => sum + areaScore(area), 0),
      data_rilevazione: dataRil,
      aree,
      cosa_manca: cosaManca.split("\n").map((x) => x.trim()).filter(Boolean),
      punti_di_forza_trasparenza: forza.split("\n").map((x) => x.trim()).filter(Boolean),
    };
    try {
      await adminUpdateBilancio(bilancio.id, { trasparenza });
      setMsg({ type: "success", text: "Scheda trasparenza pubblicata." });
      onSaved && onSaved();
      setTimeout(() => onClose(), 900);
    } catch (e) {
      setMsg({ type: "error", text: e?.response?.data?.detail || "Errore nel salvataggio." });
    } finally {
      setBusy(false);
    }
  };

  const fascia = fasciaTrasparenza(total);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 py-8 overflow-y-auto">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-md shadow-2xl w-full max-w-3xl p-6 fade-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif-display text-xl text-sq-navy">
            🔍 Trasparenza — {bilancio.societa} <span className="text-gray-400 text-sm">({bilancio.ticker})</span>
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-sq-navy"><X size={20} /></button>
        </div>

        {msg && (
          <div className={`mb-4 text-sm rounded-sm px-3 py-2 flex items-center gap-2 ${msg.type === "success" ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-red-50 border border-red-200 text-red-600"}`}>
            {msg.type === "success" && <Check size={14} />} {msg.text}
          </div>
        )}

        {/* Areas */}
        <div className="space-y-4 max-h-[45vh] overflow-y-auto pr-1">
          {TRASPARENZA_AREE.map((area) => (
            <div key={area.key} className="border border-sq rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-sq-navy text-sm">{area.label}</span>
                <span className="text-xs font-bold text-sq-orange">{areaScore(area)}/{area.max}</span>
              </div>
              <div className="space-y-2">
                {area.criteri.map((c) => (
                  <div key={c.key} className="flex items-center gap-3">
                    <label className="flex items-center gap-2 flex-1 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={crit[area.key][c.key].valore}
                        onChange={() => toggle(area.key, c)}
                        className="w-4 h-4 accent-orange-500"
                      />
                      {c.label}
                    </label>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min={0}
                        max={c.max}
                        value={crit[area.key][c.key].punti}
                        onChange={(e) => setPunti(area.key, c, e.target.value)}
                        className="w-16 border border-sq rounded-sm px-2 py-1 text-sm outline-none focus:border-sq-orange text-right"
                      />
                      <span className="text-xs text-gray-400">/ {c.max}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Text areas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-sq-navy mb-1">Cosa manca (una voce per riga)</label>
            <textarea value={cosaManca} onChange={(e) => setCosaManca(e.target.value)} rows={4}
              className="w-full border border-sq rounded-sm px-3 py-2 text-sm outline-none focus:border-sq-orange" />
          </div>
          <div>
            <label className="block text-sm font-medium text-sq-navy mb-1">Punti di forza (una voce per riga)</label>
            <textarea value={forza} onChange={(e) => setForza(e.target.value)} rows={4}
              className="w-full border border-sq rounded-sm px-3 py-2 text-sm outline-none focus:border-sq-orange" />
          </div>
        </div>

        <div className="flex items-center gap-3 mt-3">
          <label className="text-sm text-sq-navy">Data rilevazione:</label>
          <input type="date" value={dataRil} onChange={(e) => setDataRil(e.target.value)}
            className="border border-sq rounded-sm px-2 py-1.5 text-sm outline-none focus:border-sq-orange" />
        </div>

        {/* Score + actions */}
        <div className="flex flex-wrap items-center justify-between gap-4 mt-5 pt-4 border-t border-sq">
          <div className="flex items-center gap-3">
            <button onClick={calcola} className="flex items-center gap-2 border border-sq rounded-sm px-4 py-2.5 text-sm hover:border-sq-orange">
              <Calculator size={15} /> Calcola punteggio automatico
            </button>
            <span className="text-lg font-extrabold text-sq-navy">{total}/100</span>
            <span className="text-sm font-bold" style={{ color: fascia.color }}>{fascia.stars} {fascia.label}</span>
          </div>
          <div className="flex gap-3">
            <button onClick={save} disabled={busy}
              className="bg-sq-orange hover:bg-sq-orange-dark transition-colors text-white px-5 py-2.5 rounded-sm text-sm font-medium disabled:opacity-60">
              {busy ? "Pubblicazione..." : "Pubblica scheda trasparenza"}
            </button>
            <button onClick={onClose} className="border border-sq text-sq-navy px-5 py-2.5 rounded-sm text-sm hover:bg-sq-light">
              Chiudi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrasparenzaEditor;
